import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useEquipment } from '@/hooks'
import { EquipmentCreationModal, EquipmentDetailsModal } from '@/components/inspection'
import { Filter, Plus, Calendar, ChevronRight } from 'lucide-react'
import { format } from 'date-fns'
import { supabase } from '@/lib/supabase/client'
import { StatusIcon } from '@/components/ui'
import { usePageHeader } from '@/components/layout/pageHeader'
import {
  STMIDBEquipmentService,
  isSTMIDBCompany,
  mapSTMIDBToWLLCategoryCode,
} from '@/services/api/stmidbEquipmentService'
import { isSlingCategory } from '@/utils/categoryFamily'

interface EquipmentRow {
  id: string
  name: string
  part_number: string | null
  asset_number: string
  serial_number: string | null
  owner_id: string | null
  last_inspection_date: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  next_inspection_date: string | null
  status: string
  inspection_status: string | null
  description: string | null
  model: string | null
  manufacture_year: number | null
  category_id: string | null
  company_id?: string
}

interface SubMap {
  slings: string[]
  shackles: string[]
}

const EquipmentList = () => {
  const navigate = useNavigate()
  const [companyId, setCompanyId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false)
  const [selectedEquipment, setSelectedEquipment] = useState<EquipmentRow | null>(null)
  const [autoOpenId, setAutoOpenId] = useState<string | null>(null)
  const [subMap, setSubMap] = useState<Record<string, SubMap>>({})

  usePageHeader('Velg utstyr', {
    onBack: () => {
      sessionStorage.removeItem('selectedCompanyId')
      navigate('/company')
    },
  })

  useEffect(() => {
    const stored = sessionStorage.getItem('selectedCompanyId')
    if (!stored) {
      navigate('/company')
      return
    }

    const inspectionType = sessionStorage.getItem('inspectionType')
    const ndtType = sessionStorage.getItem('ndtType')
    if (inspectionType === 'ndt' && ndtType?.toUpperCase() === 'ET') {
      navigate('/et-test')
      return
    }

    const preSelected = sessionStorage.getItem('selectedEquipmentId')
    if (preSelected) {
      setAutoOpenId(preSelected)
      sessionStorage.removeItem('selectedEquipmentId')
    }

    setCompanyId(stored)
  }, [navigate])

  const { equipment, loading, error, refetch } = useEquipment(companyId || undefined)
  const isSLB = isSTMIDBCompany(companyId)

  // Fetch sub-equipment relationships once equipment list is loaded
  useEffect(() => {
    if (!equipment.length) return
    const fetchSTMIDBSubMap = async () => {
      const children = await STMIDBEquipmentService.getAllChildEquipment()
      const map: Record<string, SubMap> = {}
      for (const child of children) {
        if (!child.serial_number) continue
        if (!map[child.parent_equipment_id]) map[child.parent_equipment_id] = { slings: [], shackles: [] }
        const code = mapSTMIDBToWLLCategoryCode(child.category, child.equipment_type) || ''
        if (isSlingCategory(code)) map[child.parent_equipment_id].slings.push(child.serial_number)
        else map[child.parent_equipment_id].shackles.push(child.serial_number)
      }
      setSubMap(map)
    }
    const fetchSubMap = async () => {
      const ids = equipment.map(e => e.id)
      const { data } = await supabase
        .from('equipment_relationship_v2')
        .select(`
          main_equipment_id,
          sub_equipment:equipment_v2!equipment_relationship_v2_sub_equipment_id_fkey(
            serial_number,
            categories_v2(code)
          )
        `)
        .in('main_equipment_id', ids)

      if (!data) return
      const map: Record<string, SubMap> = {}
      for (const rel of data) {
        const mainId = rel.main_equipment_id
        if (!map[mainId]) map[mainId] = { slings: [], shackles: [] }
        const code: string = (rel.sub_equipment as any)?.categories_v2?.code || ''
        const sn: string = (rel.sub_equipment as any)?.serial_number || ''
        if (!sn) continue
        if (isSlingCategory(code)) map[mainId].slings.push(sn)
        else map[mainId].shackles.push(sn)
      }
      setSubMap(map)
    }
    if (isSLB) fetchSTMIDBSubMap()
    else fetchSubMap()
  }, [equipment, isSLB])

  // Auto-open details for pre-selected equipment once list loads
  useEffect(() => {
    if (autoOpenId && equipment.length > 0) {
      const match = equipment.find(e => e.id === autoOpenId)
      if (match) {
        setSelectedEquipment(match as unknown as EquipmentRow)
        setAutoOpenId(null)
      }
    }
  }, [autoOpenId, equipment])

  const filtered = equipment.filter(item =>
    (item.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (item.serial_number?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (item.asset_number?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (item.owner_id?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (item.part_number?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="w-10 h-10 border-4 border-[#1E3A5F] border-t-transparent rounded-full animate-spin mb-3" />
        <span className="text-base text-slate-500">Laster utstyr...</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)' }}>

      {/* ── Top bar ──────────────────────────────────────────── */}
      <div className="shrink-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center gap-3 shadow-sm">
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Søk navn, serienr, ID..."
          className="w-72 px-4 py-2.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30 focus:border-[#1E3A5F] bg-white transition-colors"
          style={{ minHeight: 44 }}
          autoFocus
        />

        {!isSLB && (
          <button
            onClick={() => setIsCreationModalOpen(true)}
            className="flex items-center gap-2 px-4 bg-[#0D9488] text-white text-sm font-semibold rounded-md hover:bg-[#0B7A70] transition-colors shadow-sm shrink-0"
            style={{ minHeight: 44 }}
            title="Opprett nytt utstyr"
          >
            <Plus size={18} />
            <span>Nytt utstyr</span>
          </button>
        )}
      </div>

      {/* ── Error ────────────────────────────────────────────── */}
      {error && (
        <div className="shrink-0 mx-5 mt-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p className="text-sm font-semibold text-red-700">{error}</p>
        </div>
      )}

      {/* ── List ─────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Filter size={40} className="text-slate-200" />
            <p className="text-base font-medium text-slate-400">
              {searchQuery ? `Ingen treff for "${searchQuery}"` : 'Ingen utstyr tilgjengelig'}
            </p>
          </div>
        ) : (
          <table className="w-full border-collapse">
            {/* Table header */}
            <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400 w-44">
                  ID
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400">
                  Utstyrsnavn
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400 w-36">
                  Siste inspeksjon
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400 w-36">
                  Siste NDT
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400 w-36">
                  Status
                </th>
                <th className="px-3 py-2.5 w-10" />
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 bg-white">
              {(filtered as unknown as EquipmentRow[]).map(item => (
                  <tr
                    key={item.id}
                    onClick={() => setSelectedEquipment(item as unknown as EquipmentRow)}
                    className="group cursor-pointer hover:bg-slate-50 transition-colors"
                    style={{ minHeight: 56 }}
                  >
                    {/* ID */}
                    <td className="px-5 py-3">
                      <p className="text-sm font-bold text-slate-800 group-hover:text-[#1E3A5F] transition-colors">
                        {item.serial_number || item.asset_number}
                      </p>
                      {item.part_number && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          <span className="text-slate-300">PN:</span> {item.part_number}
                        </p>
                      )}
                      {subMap[item.id]?.slings.map(sn => (
                        <p key={sn} className="text-[11px] text-slate-400 mt-0.5 truncate">
                          <span className="text-slate-300 mr-1">Sling</span>{sn}
                        </p>
                      ))}
                      {subMap[item.id]?.shackles.map(sn => (
                        <p key={sn} className="text-[11px] text-slate-400 mt-0.5 truncate">
                          <span className="text-slate-300 mr-1">Shackle</span>{sn}
                        </p>
                      ))}
                    </td>

                    {/* Name */}
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-700 truncate max-w-xs">
                        {item.name}
                      </p>
                    </td>

                    {/* Last inspection date */}
                    <td className="px-4 py-3">
                      {item.last_inspection_date ? (
                        <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600">
                          <Calendar size={14} className="shrink-0 text-slate-400" />
                          {format(new Date(item.last_inspection_date), 'dd.MM.yyyy')}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-300">—</span>
                      )}
                    </td>

                    {/* Last NDT date */}
                    <td className="px-4 py-3">
                      {item.last_ndt_date ? (() => {
                        const ndtDate = new Date(item.last_ndt_date)
                        const fourYearsAgo = new Date()
                        fourYearsAgo.setFullYear(fourYearsAgo.getFullYear() - 4)
                        const isOverdue = ndtDate < fourYearsAgo
                        return (
                          <span className={`text-sm font-medium ${isOverdue ? 'text-red-500' : 'text-green-600'}`}>
                            {format(ndtDate, 'yyyy')}
                          </span>
                        )
                      })() : (
                        <span className="text-sm text-slate-300">—</span>
                      )}
                    </td>

                    {/* Inspection status */}
                    <td className="px-4 py-3">
                      {item.inspection_status ? (
                        <StatusIcon status={item.inspection_status} size="md" />
                      ) : (
                        <span className="text-sm text-slate-300">—</span>
                      )}
                    </td>

                    {/* Chevron */}
                    <td className="px-3 py-3 text-right">
                      <ChevronRight
                        size={18}
                        className="text-slate-200 group-hover:text-[#1E3A5F] transition-colors inline-block"
                      />
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Row count footer */}
        {filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-slate-100 bg-white">
            <p className="text-xs text-slate-400">
              {filtered.length} utstyr{equipment.length !== filtered.length && ` (filtrert fra ${equipment.length})`}
            </p>
          </div>
        )}
      </div>

      {/* ── Details modal ─────────────────────────────────────── */}
      {selectedEquipment && (
        <EquipmentDetailsModal
          isOpen={!!selectedEquipment}
          fullscreen
          onClose={() => setSelectedEquipment(null)}
          equipment={selectedEquipment}
          onDelete={() => {
            setSelectedEquipment(null)
            refetch()
          }}
          onCopied={(created) => {
            refetch()
            setSelectedEquipment({
              id: created.id,
              name: created.name,
              part_number: created.part_number,
              asset_number: created.asset_number,
              serial_number: created.serial_number,
              owner_id: created.owner_id,
              last_inspection_date: created.last_inspection_date,
              last_ndt_date: created.last_ndt_date,
              next_ndt_date: created.next_ndt_date,
              next_inspection_date: created.next_inspection_date,
              status: created.status,
              inspection_status: created.inspection_status,
              description: created.description,
              model: created.model,
              manufacture_year: created.manufacture_year,
              category_id: created.category_id,
              company_id: created.company_id,
            })
          }}
        />
      )}

      {/* ── Creation modal ────────────────────────────────────── */}
      {companyId && !isSLB && (
        <EquipmentCreationModal
          isOpen={isCreationModalOpen}
          onClose={() => setIsCreationModalOpen(false)}
          companyId={companyId}
          onSuccess={(created) => {
            refetch()
            setSelectedEquipment({
              id: created.id,
              name: created.name,
              part_number: created.part_number,
              asset_number: created.asset_number,
              serial_number: created.serial_number,
              owner_id: created.owner_id,
              last_inspection_date: null,
              last_ndt_date: created.last_ndt_date,
              next_ndt_date: created.next_ndt_date,
              next_inspection_date: null,
              status: created.status,
              inspection_status: null,
              description: null,
              model: null,
              manufacture_year: null,
              category_id: created.category_id,
              company_id: created.company_id,
            })
          }}
        />
      )}
    </div>
  )
}

export default EquipmentList
